import { useState } from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import api from '@/services/api';
import { RefereeMemberSearch, formatMainAppUser } from '@/components/review/MainAppUserPicker';
import type { MainAppUser } from '@/types';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export default function CreateGroupDialog({ open, onClose, onCreated }: Props) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [managers, setManagers] = useState<MainAppUser[]>([]);
  const [participants, setParticipants] = useState<MainAppUser[]>([]);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setName('');
    setDescription('');
    setManagers([]);
    setParticipants([]);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const memberIds = [...managers, ...participants].map((u) => u.id);
      await api.post('/groups', {
        name,
        description,
        member_user_ids: memberIds,
        manager_user_ids: managers.map((u) => u.id),
      });
      onCreated();
      handleClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>建立裁判群組</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="群組名稱 *" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
          <TextField label="描述" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={2} />
          <Typography variant="subtitle2" fontWeight={700}>
            新增管理員{managers.length > 0 ? `（已加入 ${managers.length} 人）` : ''}
          </Typography>
          {managers.length > 0 && (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {managers.map((u) => (
                <Chip
                  key={u.id}
                  size="small"
                  color="secondary"
                  label={formatMainAppUser(u)}
                  onDelete={() => setManagers((prev) => prev.filter((x) => x.id !== u.id))}
                />
              ))}
            </Stack>
          )}
          <RefereeMemberSearch
            selected={managers}
            onSelectedChange={setManagers}
            excludeIds={[...managers, ...participants].map((u) => u.id)}
            refereesOnly
            addLabel="設為管理員"
          />
          <Typography variant="subtitle2" fontWeight={700}>
            新增參與者{participants.length > 0 ? `（已加入 ${participants.length} 人）` : ''}
          </Typography>
          {participants.length > 0 && (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {participants.map((u) => (
                <Chip
                  key={u.id}
                  size="small"
                  variant="outlined"
                  label={formatMainAppUser(u)}
                  onDelete={() => setParticipants((prev) => prev.filter((x) => x.id !== u.id))}
                />
              ))}
            </Stack>
          )}
          <RefereeMemberSearch
            selected={participants}
            onSelectedChange={setParticipants}
            excludeIds={[...managers, ...participants].map((u) => u.id)}
            refereesOnly
          />
          {(managers.length > 0 || participants.length > 0) && (
            <Typography variant="caption" color="text.secondary">
              共 {managers.length + participants.length} 位成員
              {managers.length > 0 && participants.length > 0
                ? `（${managers.length} 管理員 · ${participants.length} 參與者）`
                : managers.length > 0
                  ? '（全部為管理員）'
                  : '（全部為參與者）'}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>取消</Button>
        <Button variant="contained" color="secondary" onClick={handleCreate} disabled={!name.trim() || saving}>
          建立
        </Button>
      </DialogActions>
    </Dialog>
  );
}
